import { useState, useMemo } from 'react';
import { usePermissions } from './usePermissions';

const TAB_KEYS = { ALL: 'all' };

export const usePermissionsPage = () => {
    const [searchText, setSearchText] = useState('');
    const [activeTab, setActiveTab] = useState(TAB_KEYS.ALL);

    const { data } = usePermissions();
    const permissions = data?.permissions ?? [];

    const uniqueModules = useMemo(
        () => [...new Set(permissions.map((p) => p.module).filter(Boolean))].sort(),
        [permissions],
    );
    const uniqueFeaturesCount = useMemo(
        () => [...new Set(permissions.map((p) => p.feature).filter(Boolean))].length,
        [permissions],
    );
    const uniqueActionsCount = useMemo(
        () => [...new Set(permissions.map((p) => p.action).filter(Boolean))].length,
        [permissions],
    );

    const sliderTabItems = useMemo(
        () => [
            { key: TAB_KEYS.ALL, label: 'All' },
            ...uniqueModules.slice(0, 6).map((m) => ({
                key: m,
                label: m.charAt(0).toUpperCase() + m.slice(1),
            })),
        ],
        [uniqueModules],
    );

    const filteredPermissions = useMemo(() => {
        let list = permissions;
        if (activeTab !== TAB_KEYS.ALL) {
            list = list.filter((p) => p.module === activeTab);
        }
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            list = list.filter(
                (p) =>
                    (p.key || '').toLowerCase().includes(q) ||
                    (p.module || '').toLowerCase().includes(q) ||
                    (p.feature || '').toLowerCase().includes(q) ||
                    (p.action || '').toLowerCase().includes(q),
            );
        }
        return list;
    }, [permissions, searchText, activeTab]);

    const tableData = useMemo(
        () =>
            filteredPermissions.map((p) => ({
                key: p.id,
                id: p.id,
                keyName: p.key || `${p.module}.${p.feature}.${p.action}`,
                module: p.module ?? '—',
                feature: p.feature ?? '—',
                action: p.action ?? '—',
            })),
        [filteredPermissions],
    );

    const columns = [
        { title: 'Key', dataIndex: 'keyName', key: 'keyName' },
        { title: 'Module', dataIndex: 'module', key: 'module' },
        { title: 'Feature', dataIndex: 'feature', key: 'feature' },
        { title: 'Action', dataIndex: 'action', key: 'action' },
    ];

    return {
        searchText,
        setSearchText,
        activeTab,
        setActiveTab,
        sliderTabItems,
        tableData,
        columns,
        permissions,
        uniqueModules,
        uniqueFeaturesCount,
        uniqueActionsCount,
    };
};
